<template>
  <div class="wrong-questions-page">
    <AppBreadcrumb :items="breadcrumbItems" :showBack="true" @back="goBack">
      <template #actions>
        <div v-if="testMode && testQuestions.length > 0" class="test-header-actions">
          <a-tag color="orange" class="test-tag">
            <span class="tag-icon">❌</span>
            {{ currentIndex + 1 }} / {{ testQuestions.length }}
          </a-tag>

          <a-tag color="green" class="test-tag">
            <span class="tag-icon">✅</span>
            {{ Object.keys(selectedAnswers).length }} / {{ testQuestions.length }}
          </a-tag>

          <a-button 
            type="primary" 
            danger 
            size="small"
            @click="exitTestMode"
            class="exit-btn"
          >
            <template #icon>✕</template>
            <span class="exit-text">{{ $t('wrongQuestions.exitTest') }}</span>
          </a-button>
        </div>
      </template>
    </AppBreadcrumb>

    <!-- TEST MODE -->
    <div v-if="testMode && testQuestions.length > 0" class="test-mode">
      <div class="test-content-wrapper">
        <!-- Savol va rasm qismi - scrollable area -->
        <div class="test-scroll-area">
          <a-card class="custom-card question-card">
            <template #title>
              <div class="card-title">
                <span class="title-text">{{ $t('wrongQuestions.testTitle') }}</span>
                <a-tag color="orange" class="mobile-tag">⚠️ {{ $t('wrongQuestions.wrongTag') }}</a-tag>
              </div>
            </template>

            <!-- Oldingi xato javob -->
            <div v-if="currentQuestion?.previous_wrong_answer" class="wrong-info">
              <div class="wrong-answer-box">
                <span class="wrong-label">{{ $t('wrongQuestions.youMarkedWrong') }}</span>
                <span class="wrong-answer">{{ currentQuestion.previous_wrong_answer.text }}</span>
              </div>
            </div>

            <!-- Savol matni -->
            <h3 class="question-text">{{ currentQuestion?.text || $t('wrongQuestions.questionNotFound') }}</h3>

            <!-- Variantlar -->
            <div class="options-list">
              <div
                v-for="(option, idx) in currentQuestion?.options || []"
                :key="option.id"
                class="option-item"
                :class="getOptionClass(option)"
                @click="selectAnswer(option)"
              >
                <span class="option-letter">{{ getOptionLetter(idx) }}</span>
                <span class="option-content">{{ option.text }}</span>
                <span v-if="selectedAnswers[currentQuestion?.id] === option.id" class="selected-check">✓</span>
              </div>
            </div>
          </a-card>

          <!-- Rasm va tushuntirish qismi -->
          <a-card class="custom-card info-card">
            <!-- Rasm -->
            <div class="image-container">
              <img
                v-if="currentQuestion?.image_url"
                :src="currentQuestion.image_url"
                class="question-image"
                alt="Savol rasmi"
              />
              <div v-else class="no-image">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" stroke-width="1.5"/>
                  <path d="M22 12L16 6" stroke="currentColor" stroke-width="1.5"/>
                  <path d="M2 12L8 6" stroke="currentColor" stroke-width="1.5"/>
                  <circle cx="8" cy="10" r="2" fill="currentColor"/>
                </svg>
                <p>{{ $t('wrongQuestions.noImage') }}</p>
              </div>
            </div>

            <!-- Tushuntirish -->
            <div v-if="showExplanation" class="explanation-box">
              <div class="explanation-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" stroke-width="1.5"/>
                  <path d="M7 10L12 15L17 10" stroke="currentColor" stroke-width="1.5"/>
                  <path d="M12 15V3" stroke="currentColor" stroke-width="1.5"/>
                </svg>
                Tushuntirish
              </div>
              <div class="explanation-content">{{ currentQuestion?.explanation || $t('wrongQuestions.noExplanation') }}</div>
              
              <div v-if="isCurrentAnswerCorrect" class="correct-message">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
                {{ $t('wrongQuestions.correctRemoved') }}
              </div>
              <div v-else-if="isCurrentAnswerWrong" class="wrong-message">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
                {{ $t('wrongQuestions.wrongRetry') }}
              </div>
            </div>

            <!-- Progress -->
            <div class="progress-section">
              <div class="progress-info">
                <span>{{ $t('wrongQuestions.answered') }}: {{ Object.keys(selectedAnswers).length }} / {{ testQuestions.length }}</span>
                <span class="progress-percent">{{ Math.round((Object.keys(selectedAnswers).length / testQuestions.length) * 100) }}%</span>
              </div>
              <a-progress 
                :percent="Math.round((Object.keys(selectedAnswers).length / testQuestions.length) * 100)" 
                :show-info="false"
                strokeColor="#f59e0b"
                trailColor="#e8e8e8"
              />
            </div>

            <!-- Yakunlash tugmasi -->
            <a-button
              v-if="Object.keys(selectedAnswers).length === testQuestions.length"
              type="primary"
              block
              size="large"
              class="finish-button"
              :loading="finishLoading"
              @click="finishTest"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 13L9 17L19 7" stroke="white" stroke-width="2" stroke-linecap="round"/>
              </svg>
              Testni yakunlash
            </a-button>
          </a-card>
        </div>

        <!-- Savol raqamlari - fixed bottom scrollable -->
        <div class="question-numbers-wrapper">
          <div class="question-numbers">
            <div class="numbers-grid">
              <button
                v-for="(q, index) in testQuestions"
                :key="q.id"
                class="number-btn"
                :class="getNumberButtonClass(index)"
                @click="goToQuestion(index)"
              >
                {{ index + 1 }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- LIST MODE (xato savollar ro'yxati) -->
    <div v-else class="list-mode">
      <a-spin :spinning="loading">
        <div v-if="wrongQuestions && wrongQuestions.length > 0">
          <!-- Test boshlash tugmasi -->
          <div class="start-test-section">
            <a-button 
              type="primary" 
              size="large" 
              class="start-test-btn"
              @click="startWrongTest"
              :loading="starting"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 3L19 12L5 21V3Z" stroke="white" stroke-width="1.5" fill="white"/>
              </svg>
              {{ $t('wrongQuestions.startTest', { count: totalWrongCount }) }}
            </a-button>
            <p class="start-test-note">
              {{ $t('wrongQuestions.startNote') }}
            </p>
          </div>
        </div>

        <div v-else class="empty-state">
          <div class="empty-icon">✅</div>
          <h3>{{ $t('wrongQuestions.emptyTitle') }}</h3>
          <p>{{ $t('wrongQuestions.emptySubtitle') }}</p>
          <a-button type="primary" @click="$router.push('/')">{{ $t('wrongQuestions.backToHome') }}</a-button>
        </div>
      </a-spin>
    </div>
  </div>
</template>

<script>
import api, { MEDIA_BASE_URL } from '@/api/axios'
import AppBreadcrumb from '@/components/AppBreadcrumb.vue'
import { message } from 'ant-design-vue'

export default {
  name: 'WrongQuestionsView',
  components: { AppBreadcrumb },

  data() {
    return {
      loading: false,
      starting: false,
      finishLoading: false,
      wrongQuestions: [],
      groupedQuestions: [],
      expandedQuestion: null,
      
      // Test mode
      testMode: false,
      testQuestions: [],
      testResultId: null,
      testWrongIds: [],
      currentIndex: 0,
      selectedAnswers: {},
      showExplanation: false,
      startedAt: null,
      resolvedWrongQuestions: []
    }
  },

  computed: {
    breadcrumbItems() {
      return [{ label: this.$t('wrongQuestions.breadcrumb') }]
    },

    totalWrongCount() {
      return this.wrongQuestions?.length || 0
    },

    testResultsCount() {
      return this.groupedQuestions?.length || 0
    },

    uniqueQuestionsCount() {
      if (!this.wrongQuestions || !this.wrongQuestions.length) return 0
      const uniqueIds = new Set(this.wrongQuestions.map(q => q.question_id))
      return uniqueIds.size
    },

    currentQuestion() {
      return this.testQuestions[this.currentIndex] || null
    },

    isCurrentAnswerCorrect() {
      if (!this.currentQuestion) return false
      const selectedId = this.selectedAnswers[this.currentQuestion.id]
      const correctOption = this.currentQuestion.options?.find(o => o.is_correct)
      return selectedId && correctOption && selectedId === correctOption.id
    },

    isCurrentAnswerWrong() {
      if (!this.currentQuestion) return false
      const selectedId = this.selectedAnswers[this.currentQuestion.id]
      const correctOption = this.currentQuestion.options?.find(o => o.is_correct)
      return selectedId && correctOption && selectedId !== correctOption.id
    }
  },

  mounted() {
    this.fetchWrongQuestions()
    window.addEventListener('keydown', this.handleKeyDown)
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown)
  },

  methods: {
    getAuthHeaders() {
      const token = localStorage.getItem('accessToken')
      return {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      }
    },

    getOptionLetter(index) {
      return String.fromCharCode(65 + index)
    },

    async fetchWrongQuestions() {
      this.loading = true
      try {
        const response = await api.get(
          `/v1/tests/wrong-questions/`,
          { headers: this.getAuthHeaders() }
        )
        
        if (response.data) {
          if (Array.isArray(response.data)) {
            this.wrongQuestions = response.data
            this.groupQuestionsByTest()
          } else if (response.data.questions) {
            this.wrongQuestions = response.data.questions
            this.groupQuestionsByTest()
          } else {
            this.wrongQuestions = []
            this.groupedQuestions = []
          }
        }
      } catch (error) {
        console.error('Xato savollarni olishda xatolik:', error)
        message.error(this.$t('wrongQuestions.fetchError'))
      } finally {
        this.loading = false
      }
    },

    groupQuestionsByTest() {
      if (!this.wrongQuestions || !this.wrongQuestions.length) {
        this.groupedQuestions = []
        return
      }

      const groups = {}
      
      this.wrongQuestions.forEach(q => {
        const testId = q.test_result_id || 'unknown'
        if (!groups[testId]) {
          groups[testId] = {
            test_id: testId,
            test_date: q.test_date || new Date().toISOString(),
            score: q.test_score || 0,
            questions: []
          }
        }
        groups[testId].questions.push(q)
      })

      this.groupedQuestions = Object.values(groups)
    },

    async startWrongTest() {
      if (this.starting) return
      
      this.starting = true
      try {
        const response = await api.post(
          `/v1/tests/wrong-questions-start/`,
          {},
          { headers: this.getAuthHeaders() }
        )

        if (response.data && response.data.questions) {
          this.testQuestions = response.data.questions
          this.testResultId = response.data.result?.id
          this.testWrongIds = response.data.meta?.wrong_question_ids || []
          
          this.testMode = true
          this.currentIndex = 0
          this.selectedAnswers = {}
          this.showExplanation = false
          this.startedAt = Date.now()
          this.resolvedWrongQuestions = []
          
          message.success(this.$t('wrongQuestions.testStarted', { count: this.testQuestions.length }))
        } else {
          message.warning(this.$t('wrongQuestions.noWrongForTest'))
        }
      } catch (error) {
        console.error('Test boshlashda xatolik:', error)
        message.error(
          error?.response?.data?.detail || this.$t('wrongQuestions.startError')
        )
      } finally {
        this.starting = false
      }
    },

    exitTestMode() {
      this.testMode = false
      this.testQuestions = []
      this.testResultId = null
      this.testWrongIds = []
      this.selectedAnswers = {}
      this.showExplanation = false
    },

    handleKeyDown(e) {
      if (!this.testMode || !this.currentQuestion) return
      
      const key = e.key
      if (key >= '1' && key <= '6') {
        const index = parseInt(key) - 1
        const option = this.currentQuestion?.options?.[index]
        if (option && !this.selectedAnswers[this.currentQuestion.id]) {
          this.selectAnswer(option)
        }
      } else if (key === 'F1' || key === 'F2' || key === 'F3' || key === 'F4' || key === 'F5' || key === 'F6') {
        e.preventDefault()
        const index = parseInt(key.substring(1)) - 1
        const option = this.currentQuestion?.options?.[index]
        if (option && !this.selectedAnswers[this.currentQuestion.id]) {
          this.selectAnswer(option)
        }
      }
    },

    selectAnswer(option) {
      if (!this.testMode || !this.currentQuestion || this.selectedAnswers[this.currentQuestion.id]) {
        return
      }

      this.selectedAnswers[this.currentQuestion.id] = option.id
      this.showExplanation = true

      if (option.is_correct) {
        const wrongQuestionId = this.currentQuestion.wrong_question_id
        if (wrongQuestionId && !this.resolvedWrongQuestions.includes(wrongQuestionId)) {
          this.resolvedWrongQuestions.push(wrongQuestionId)
          this.resolveWrongQuestion(wrongQuestionId, option.id)
        }
      }

      setTimeout(() => {
        if (this.currentIndex < this.testQuestions.length - 1) {
          this.currentIndex++
          this.showExplanation = !!this.selectedAnswers[this.testQuestions[this.currentIndex]?.id]
        }
      }, 1500)
    },

    async resolveWrongQuestion(wrongQuestionId, selectedOptionId) {
      try {
        await api.post(
          `/v1/tests/wrong-question-resolve/${wrongQuestionId}/`,
          { selected_option_id: selectedOptionId },
          { headers: this.getAuthHeaders() }
        )
      } catch (error) {
        console.error('Xato savolni o\'chirishda xatolik:', error)
      }
    },

    getOptionClass(option) {
      if (!this.currentQuestion) return []
      
      const classes = ['option-item']
      const selectedId = this.selectedAnswers[this.currentQuestion?.id]
      const correctOption = this.currentQuestion?.options?.find(o => o.is_correct)

      if (selectedId) {
        if (option.id === selectedId) {
          classes.push('selected')
          if (option.is_correct) {
            classes.push('correct')
          } else {
            classes.push('wrong')
          }
        }
        if (option.is_correct && selectedId !== correctOption?.id) {
          classes.push('show-correct')
        }
      }

      return classes
    },

    getNumberButtonClass(index) {
      const classes = ['number-btn']
      const question = this.testQuestions[index]
      
      if (this.currentIndex === index) {
        classes.push('active')
      }
      
      if (question && this.selectedAnswers[question.id]) {
        const correctOption = question.options?.find(o => o.is_correct)
        if (this.selectedAnswers[question.id] === correctOption?.id) {
          classes.push('correct')
        } else {
          classes.push('wrong')
        }
      }
      
      return classes
    },

    goToQuestion(index) {
      this.currentIndex = index
      this.showExplanation = !!this.selectedAnswers[this.testQuestions[index]?.id]
    },

    buildAnswersPayload() {
      return this.testQuestions.map(q => ({
        question_id: q.id,
        selected_option_id: this.selectedAnswers[q.id] || null
      }))
    },

    async finishTest() {
      if (this.finishLoading) return

      this.finishLoading = true
      try {
        const durationSeconds = Math.floor((Date.now() - this.startedAt) / 1000)

        await api.post(
          `/v1/tests/finish/`,
          {
            result_id: this.testResultId,
            duration_seconds: durationSeconds,
            answers: this.buildAnswersPayload()
          },
          { headers: this.getAuthHeaders() }
        )

        message.success(this.$t('wrongQuestions.testFinished'))
        this.exitTestMode()
        this.fetchWrongQuestions()
      } catch (error) {
        console.error('Testni yakunlashda xatolik:', error)
        message.error(this.$t('wrongQuestions.finishError'))
      } finally {
        this.finishLoading = false
      }
    },

    toggleExplanation(id) {
      this.expandedQuestion = this.expandedQuestion === id ? null : id
    },

    formatDate(dateString) {
      if (!dateString) return this.$t('wrongQuestions.unknownDate')
      const date = new Date(dateString)
      return date.toLocaleDateString('uz-UZ', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    getScoreColor(score) {
      if (score >= 80) return 'green'
      if (score >= 60) return 'blue'
      if (score >= 40) return 'orange'
      return 'red'
    },

    goBack() {
      if (this.testMode) {
        this.exitTestMode()
      } else {
        this.$router.push('/')
      }
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.wrong-questions-page {
  min-height: 100vh;
  background: var(--bg);
  overflow-x: hidden;
}

/* Test mode styles */
.test-mode {
  position: relative;
  height: calc(100vh - 60px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.test-content-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

/* Scrollable area - main content */
.test-scroll-area {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 16px 80px 16px;
  -webkit-overflow-scrolling: touch;
}

@media (min-width: 768px) {
  .test-scroll-area {
    padding: 20px 24px 90px 24px;
  }
}

/* Hide scrollbar for Chrome, Safari and Opera */
.test-scroll-area::-webkit-scrollbar {
  width: 4px;
}

.test-scroll-area::-webkit-scrollbar-track {
  background: var(--border);
  border-radius: 4px;
}

.test-scroll-area::-webkit-scrollbar-thumb {
  background: var(--primary);
  border-radius: 4px;
}

/* Question numbers wrapper - fixed at bottom */
.question-numbers-wrapper {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--bg);
  border-top: 1px solid var(--border);
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.05);
  z-index: 100;
}

.question-numbers {
  padding: 12px;
  background: var(--bg);
}

@media (min-width: 480px) {
  .question-numbers {
    padding: 16px;
  }
}

.numbers-grid {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 4px 0;
  max-width: 100%;
  margin: 0 auto;
  scrollbar-width: thin;
  -webkit-overflow-scrolling: touch;
}

.numbers-grid::-webkit-scrollbar {
  height: 3px;
}

.numbers-grid::-webkit-scrollbar-track {
  background: var(--border);
  border-radius: 10px;
}

.numbers-grid::-webkit-scrollbar-thumb {
  background: var(--primary);
  border-radius: 10px;
}

.number-btn {
  min-width: 36px;
  height: 36px;
  border: 1px solid var(--border);
  background: var(--bg-card);
  color: var(--text);
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s;
  font-size: 12px;
  flex-shrink: 0;
}

@media (min-width: 480px) {
  .number-btn {
    min-width: 40px;
    height: 40px;
    font-size: 14px;
  }
}

.number-btn:hover {
  border-color: var(--primary);
  transform: translateY(-2px);
}

.number-btn.active {
  background: var(--primary);
  color: white;
  border-color: var(--primary);
}

.number-btn.correct {
  background: #22c55e;
  color: white;
  border-color: #22c55e;
}

.number-btn.wrong {
  background: #ef4444;
  color: white;
  border-color: #ef4444;
}

/* Cards */
.question-card, .info-card {
  margin-bottom: 16px;
  border-radius: 16px;
  overflow: hidden;
}

@media (min-width: 768px) {
  .question-card, .info-card {
    margin-bottom: 20px;
  }
}

:deep(.ant-card-body) {
  padding: 16px;
}

@media (min-width: 480px) {
  :deep(.ant-card-body) {
    padding: 20px;
  }
}

.card-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;
  gap: 8px;
}

.title-text {
  font-size: 14px;
  font-weight: 600;
}

@media (min-width: 480px) {
  .title-text {
    font-size: 16px;
  }
}

.mobile-tag {
  font-size: 11px;
  padding: 2px 8px;
}

.test-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

@media (min-width: 480px) {
  .test-header-actions {
    gap: 12px;
  }
}

.test-tag {
  font-size: 12px;
  padding: 2px 8px;
  margin: 0;
}

@media (min-width: 480px) {
  .test-tag {
    font-size: 14px;
    padding: 4px 12px;
  }
}

.exit-btn {
  padding: 4px 10px;
  height: auto;
}

.exit-text {
  display: none;
}

@media (min-width: 480px) {
  .exit-text {
    display: inline;
  }
  
  .exit-btn {
    padding: 4px 15px;
  }
}

.tag-icon {
  margin-right: 4px;
}

/* Wrong info */
.wrong-info {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid #ef4444;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 16px;
}

@media (min-width: 480px) {
  .wrong-info {
    padding: 12px;
    margin-bottom: 20px;
  }
}

.wrong-label {
  color: #ef4444;
  font-weight: 600;
  margin-right: 8px;
  font-size: 12px;
}

.wrong-answer {
  color: var(--text);
  font-size: 13px;
}

.question-text {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 16px;
  line-height: 1.5;
  color: var(--text);
}

@media (min-width: 480px) {
  .question-text {
    font-size: 18px;
    margin-bottom: 20px;
  }
}

/* Options */
.options-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

@media (min-width: 480px) {
  .options-list {
    gap: 12px;
  }
}

.option-item {
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.2s;
  background: var(--bg-card);
}

@media (min-width: 480px) {
  .option-item {
    padding: 12px 16px;
    gap: 12px;
  }
}

.option-item:hover {
  border-color: var(--primary);
  transform: translateX(2px);
}

.option-letter {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--primary);
  color: white;
  border-radius: 6px;
  font-weight: 600;
  font-size: 12px;
  flex-shrink: 0;
}

@media (min-width: 480px) {
  .option-letter {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }
}

.option-content {
  flex: 1;
  color: var(--text);
  font-size: 13px;
  line-height: 1.4;
}

@media (min-width: 480px) {
  .option-content {
    font-size: 14px;
  }
}

.selected-check {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #22c55e;
  color: white;
  border-radius: 50%;
  font-size: 12px;
  flex-shrink: 0;
}

/* Image */
.image-container {
  width: 100%;
  min-height: 180px;
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-card);
  margin-bottom: 16px;
  padding: 12px;
}

@media (min-width: 480px) {
  .image-container {
    min-height: 200px;
    margin-bottom: 20px;
    padding: 16px;
  }
}

@media (min-width: 768px) {
  .image-container {
    min-height: 250px;
  }
}

.question-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.no-image {
  color: var(--text-soft);
  font-size: 14px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.no-image svg {
  color: var(--text-soft);
  opacity: 0.5;
}

/* Explanation */
.explanation-box {
  padding: 12px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid var(--primary);
  border-radius: 12px;
  margin-bottom: 16px;
}

@media (min-width: 480px) {
  .explanation-box {
    padding: 16px;
    margin-bottom: 20px;
  }
}

.explanation-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--primary);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.explanation-content {
  color: var(--text);
  line-height: 1.6;
  font-size: 13px;
}

@media (min-width: 480px) {
  .explanation-content {
    font-size: 14px;
  }
}

.correct-message,
.wrong-message {
  margin-top: 12px;
  padding: 10px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

@media (min-width: 480px) {
  .correct-message,
  .wrong-message {
    margin-top: 16px;
    padding: 12px;
    font-size: 13px;
  }
}

.correct-message {
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid #22c55e;
  color: #22c55e;
}

.wrong-message {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid #ef4444;
  color: #ef4444;
}

/* Progress */
.progress-section {
  margin-top: 16px;
  padding: 12px;
  background: var(--bg-card);
  border-radius: 12px;
}

@media (min-width: 480px) {
  .progress-section {
    margin-top: 20px;
    padding: 16px;
  }
}

.progress-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  color: var(--text);
  font-size: 12px;
}

@media (min-width: 480px) {
  .progress-info {
    font-size: 14px;
  }
}

.progress-percent {
  font-weight: 600;
  color: var(--primary);
}

.finish-button {
  margin-top: 16px;
  height: 44px;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

@media (min-width: 480px) {
  .finish-button {
    margin-top: 20px;
    height: 48px;
    font-size: 16px;
  }
}

:deep(.ant-progress-inner) {
  height: 6px !important;
}

@media (min-width: 480px) {
  :deep(.ant-progress-inner) {
    height: 8px !important;
  }
}

/* List mode */
.list-mode {
  padding: 16px;
  min-height: calc(100vh - 60px);
  overflow-y: auto;
}

@media (min-width: 768px) {
  .list-mode {
    padding: 20px;
  }
}

.start-test-section {
  text-align: center;
  margin: 20px 0;
  padding: 24px 16px;
  background: linear-gradient(145deg, var(--bg-card), var(--bg-card-2));
  border: 1px solid var(--border);
  border-radius: 20px;
}

@media (min-width: 480px) {
  .start-test-section {
    margin: 32px 0;
    padding: 32px;
  }
}

.start-test-btn {
  min-width: 280px;
  height: 44px;
  font-size: 14px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

@media (min-width: 480px) {
  .start-test-btn {
    min-width: 300px;
    height: 48px;
    font-size: 16px;
  }
}

.start-test-note {
  margin-top: 12px;
  color: var(--text-soft);
  font-size: 12px;
  padding: 0 8px;
}

@media (min-width: 480px) {
  .start-test-note {
    font-size: 14px;
  }
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
  animation: float 3s ease-in-out infinite;
}

.empty-state h3 {
  font-size: 18px;
  margin-bottom: 8px;
  color: var(--text);
}

.empty-state p {
  font-size: 14px;
  color: var(--text-soft);
  margin-bottom: 20px;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}
</style>